// src/components/headerone/Main.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SharedHeader from './SharedHeader';
import AWSBot from './bots/AWSBot';
import GenesysBot from './bots/GenesysBot';
import OmniBot from './bots/OmniBot';
import Omni8x8Bot from './bots/Omni8x8Bot';
import EightByEightBot from './bots/EightByEightBot';
import OmniBot2 from './bots/OmniBot2';
import OmniBot2Demo from './bots/OmniBot2Demo';
import Five9Bot from './bots/Five9Bot';

const STORAGE_KEY = 'gts.header.variant';

const VARIANTS = [
  { id: 'aws',       label: 'AWS Lex',          botComponent: AWSBot },
  { id: 'genesys',   label: 'Genesys',          botComponent: GenesysBot },
  { id: 'omni',      label: 'Omni Genesys',     botComponent: OmniBot },
  { id: 'omni8x8',   label: 'Omni 8x8',         botComponent: Omni8x8Bot },
  { id: '8x8',       label: '8x8',              botComponent: EightByEightBot },
  { id: 'omni2',     label: 'Omnibot 2.0',      botComponent: OmniBot2 },
  { id: 'omni2demo', label: 'Omnibots 2.0 Demo', botComponent: OmniBot2Demo },
  { id: 'five9',     label: 'Five9',             botComponent: Five9Bot },
];

/* ---------- soft show/hide helpers ---------- */
const softHide = (el) => { if (!el) return; el.style.display = 'none'; el.style.opacity = '0'; el.style.visibility = 'hidden'; };
const unhide   = (el) => { if (!el) return; el.style.removeProperty('display'); el.style.removeProperty('opacity'); el.style.removeProperty('visibility'); };

const showBySelectors = (sels) => { sels.forEach((s) => document.querySelectorAll(s).forEach(unhide)); };
const hideBySelectors = (sels) => {
  sels.forEach((s) => {
    document.querySelectorAll(s).forEach((n) => {
      n.classList?.remove('open', 'is-open', 'visible', 'expanded', 'active');
      softHide(n);
    });
  });
};

/* ---------- tag Lex container so CSS can hide only Lex, not Omni ---------- */
const tagLexContainer = () => {
  const el = document.querySelector('.chat-widget-container');
  if (el) el.setAttribute('data-bot', 'aws');
};

/* ---------- injected CSS gate ---------- */
const ensureBotVisibilityStyles = () => {
  const ID = 'gts-active-bot-visibility-styles';
  if (document.getElementById(ID)) return;
  const css = `
    /* Genesys active: hide Lex (aws-tagged), Omni Genesys, Omni 8x8, 8x8 */
    body[data-gts-active-bot="genesys"]  .chat-widget-container[data-bot="aws"],
    body[data-gts-active-bot="genesys"]  #mainButton,
    body[data-gts-active-bot="genesys"]  #iframe-id,
    body[data-gts-active-bot="genesys"]  #question-box,
    body[data-gts-active-bot="genesys"]  .df-message-box,
    body[data-gts-active-bot="genesys"]  #eightbyeight-widget,
    body[data-gts-active-bot="genesys"]  .eightbyeight-launcher,
    body[data-gts-active-bot="genesys"]  iframe[src*="8x8"] { display:none !important; visibility:hidden !important; opacity:0 !important; }

    /* AWS Lex active: hide Genesys, Omni Genesys, Omni 8x8, 8x8 */
    body[data-gts-active-bot="aws"]      #genesys-messenger,
    body[data-gts-active-bot="aws"]      #genesys-messenger-button,
    body[data-gts-active-bot="aws"]      iframe[id^="genesys-messenger"],
    body[data-gts-active-bot="aws"]      #mainButton,
    body[data-gts-active-bot="aws"]      #iframe-id,
    body[data-gts-active-bot="aws"]      #question-box,
    body[data-gts-active-bot="aws"]      .df-message-box,
    body[data-gts-active-bot="aws"]      #eightbyeight-widget,
    body[data-gts-active-bot="aws"]      .eightbyeight-launcher,
    body[data-gts-active-bot="aws"]      iframe[src*="8x8"] { display:none !important; visibility:hidden !important; opacity:0 !important; }

    /* Omni Genesys active: hide Genesys, Lex (aws-tagged), Omni 8x8, 8x8, Omnibot 2.0 */
    body[data-gts-active-bot="omni"]     #genesys-messenger,
    body[data-gts-active-bot="omni"]     #genesys-messenger-button,
    body[data-gts-active-bot="omni"]     iframe[id^="genesys-messenger"],
    body[data-gts-active-bot="omni"]     .chat-widget-container[data-bot="aws"],
    body[data-gts-active-bot="omni"]     #eightbyeight-widget,
    body[data-gts-active-bot="omni"]     .eightbyeight-launcher,
    body[data-gts-active-bot="omni"]     iframe[src*="8x8"],
    body[data-gts-active-bot="omni"]     [data-omnibot-widget],
    body[data-gts-active-bot="omni"]     .omnibot-widget,
    body[data-gts-active-bot="omni"]     #omnibot-widget,
    body[data-gts-active-bot="omni"]     iframe[src*="omnibots-widget"] { display:none !important; visibility:hidden !important; opacity:0 !important; }

    /* Omni Genesys active: explicitly show Omni Genesys bot elements */
    body[data-gts-active-bot="omni"]     #mainButton,
    body[data-gts-active-bot="omni"]     #iframe-id,
    body[data-gts-active-bot="omni"]     #question-box,
    body[data-gts-active-bot="omni"]     .df-message-box { display:block !important; visibility:visible !important; opacity:1 !important; }

    /* Omni 8x8 active: hide Genesys, Lex (aws-tagged), Omni Genesys, 8x8 */
    body[data-gts-active-bot="omni8x8"]  #genesys-messenger,
    body[data-gts-active-bot="omni8x8"]  #genesys-messenger-button,
    body[data-gts-active-bot="omni8x8"]  iframe[id^="genesys-messenger"],
    body[data-gts-active-bot="omni8x8"]  .chat-widget-container[data-bot="aws"],
    body[data-gts-active-bot="omni8x8"]  #eightbyeight-widget,
    body[data-gts-active-bot="omni8x8"]  .eightbyeight-launcher,
    body[data-gts-active-bot="omni8x8"]  iframe[src*="8x8"] { display:none !important; visibility:hidden !important; opacity:0 !important; }

    /* Omni 8x8 active: explicitly show Omni 8x8 bot elements */
    body[data-gts-active-bot="omni8x8"]  #mainButton,
    body[data-gts-active-bot="omni8x8"]  #iframe-id,
    body[data-gts-active-bot="omni8x8"]  #question-box,
    body[data-gts-active-bot="omni8x8"]  .df-message-box { display:block !important; visibility:visible !important; opacity:1 !important; }

    /* 8x8 active: hide Genesys, Lex (aws-tagged), Omni Genesys, Omni 8x8, Omnibot 2.0 */
    body[data-gts-active-bot="8x8"]      #genesys-messenger,
    body[data-gts-active-bot="8x8"]      #genesys-messenger-button,
    body[data-gts-active-bot="8x8"]      iframe[id^="genesys-messenger"],
    body[data-gts-active-bot="8x8"]      .chat-widget-container[data-bot="aws"],
    body[data-gts-active-bot="8x8"]      #mainButton,
    body[data-gts-active-bot="8x8"]      #iframe-id,
    body[data-gts-active-bot="8x8"]      #question-box,
    body[data-gts-active-bot="8x8"]      .df-message-box,
    body[data-gts-active-bot="8x8"]      [data-omnibot-widget],
    body[data-gts-active-bot="8x8"]      .omnibot-widget,
    body[data-gts-active-bot="8x8"]      #omnibot-widget,
    body[data-gts-active-bot="8x8"]      iframe[src*="omnibots-widget"] { display:none !important; visibility:hidden !important; opacity:0 !important; }

    /* Omnibot 2.0 active: hide Genesys, Lex (aws-tagged), Omni Genesys, Omni 8x8, 8x8 */
    body[data-gts-active-bot="omni2"]    #genesys-messenger,
    body[data-gts-active-bot="omni2"]    #genesys-messenger-button,
    body[data-gts-active-bot="omni2"]    iframe[id^="genesys-messenger"],
    body[data-gts-active-bot="omni2"]    .chat-widget-container[data-bot="aws"],
    body[data-gts-active-bot="omni2"]    #mainButton,
    body[data-gts-active-bot="omni2"]    #iframe-id,
    body[data-gts-active-bot="omni2"]    #question-box,
    body[data-gts-active-bot="omni2"]    .df-message-box,
    body[data-gts-active-bot="omni2"]    #eightbyeight-widget,
    body[data-gts-active-bot="omni2"]    .eightbyeight-launcher,
    body[data-gts-active-bot="omni2"]    iframe[src*="8x8"]:not([src*="omnibots-widget"]) { display:none !important; visibility:hidden !important; opacity:0 !important; }

    /* Omnibot 2.0 active: explicitly show Omnibot 2.0 widget elements */
    body[data-gts-active-bot="omni2"]    [data-omnibot-widget],
    body[data-gts-active-bot="omni2"]    .omnibot-widget,
    body[data-gts-active-bot="omni2"]    #omnibot-widget,
    body[data-gts-active-bot="omni2"]    iframe[src*="omnibots-widget"] { display:block !important; visibility:visible !important; opacity:1 !important; }

    /* Omnibots 2.0 Demo active: same hide/show rules as omni2 */
    body[data-gts-active-bot="omni2demo"]    #genesys-messenger,
    body[data-gts-active-bot="omni2demo"]    #genesys-messenger-button,
    body[data-gts-active-bot="omni2demo"]    iframe[id^="genesys-messenger"],
    body[data-gts-active-bot="omni2demo"]    .chat-widget-container[data-bot="aws"],
    body[data-gts-active-bot="omni2demo"]    #mainButton,
    body[data-gts-active-bot="omni2demo"]    #iframe-id,
    body[data-gts-active-bot="omni2demo"]    #question-box,
    body[data-gts-active-bot="omni2demo"]    .df-message-box,
    body[data-gts-active-bot="omni2demo"]    #eightbyeight-widget,
    body[data-gts-active-bot="omni2demo"]    .eightbyeight-launcher,
    body[data-gts-active-bot="omni2demo"]    iframe[src*="8x8"]:not([src*="omnibots-widget"]) { display:none !important; visibility:hidden !important; opacity:0 !important; }

    body[data-gts-active-bot="omni2demo"]    [data-omnibot-widget],
    body[data-gts-active-bot="omni2demo"]    .omnibot-widget,
    body[data-gts-active-bot="omni2demo"]    #omnibot-widget,
    body[data-gts-active-bot="omni2demo"]    iframe[src*="omnibots-widget"] { display:block !important; visibility:visible !important; opacity:1 !important; }

    /* Five9 active: hide Genesys, Lex (aws-tagged), Omni Genesys/8x8, 8x8, Omnibot 2.0 */
    body[data-gts-active-bot="five9"]    #genesys-messenger,
    body[data-gts-active-bot="five9"]    #genesys-messenger-button,
    body[data-gts-active-bot="five9"]    iframe[id^="genesys-messenger"],
    body[data-gts-active-bot="five9"]    .chat-widget-container[data-bot="aws"],
    body[data-gts-active-bot="five9"]    #mainButton,
    body[data-gts-active-bot="five9"]    #iframe-id,
    body[data-gts-active-bot="five9"]    #question-box,
    body[data-gts-active-bot="five9"]    .df-message-box,
    body[data-gts-active-bot="five9"]    #eightbyeight-widget,
    body[data-gts-active-bot="five9"]    .eightbyeight-launcher,
    body[data-gts-active-bot="five9"]    iframe[src*="8x8"],
    body[data-gts-active-bot="five9"]    [data-omnibot-widget],
    body[data-gts-active-bot="five9"]    .omnibot-widget,
    body[data-gts-active-bot="five9"]    #omnibot-widget,
    body[data-gts-active-bot="five9"]    iframe[src*="omnibots-widget"] { display:none !important; visibility:hidden !important; opacity:0 !important; }

    /* Five9 widget: hidden whenever Five9 is NOT the active bot (keeps it out of other demos) */
    body:not([data-gts-active-bot="five9"]) iframe[src*="five9"],
    body:not([data-gts-active-bot="five9"]) [id*="five9"],
    body:not([data-gts-active-bot="five9"]) [class*="five9"] { display:none !important; visibility:hidden !important; opacity:0 !important; }

    /* Bot positioning: Ensure opened chat widgets float above the bottom of the page */
    /* Omni bots - opened chat widget container when expanded */
    /* Target the mainButton container when aria-expanded="true" (chat is open) */
    /* This moves the entire widget (button + iframe) up and to the right when chat is open */
    #mainButton[aria-expanded="true"] {
      bottom: 80px !important;
      right: 20px !important;
    }

    /* 8x8 bot - opened chat widget iframe (not the launcher button) */
    /* Target 8x8 chat container iframe when it's visible */
    iframe.__8x8Chat-Container,
    iframe[src*="8x8.com"]:not([src*="omnibots"]) {
      bottom: 80px !important;
    }
  `;
  const style = document.createElement('style');
  style.id = ID;
  style.textContent = css;
  document.head.appendChild(style);
};

/* ---------- selectors per bot ---------- */
const OMNI_SELECTORS    = ['#mainButton', '#iframe-id', '#question-box', '.df-message-box'];
const OMNI8x8_SELECTORS  = ['#mainButton', '#iframe-id', '#question-box', '.df-message-box'];
const GENESYS_SELECTORS = ['#genesys-messenger', '#genesys-messenger-button', 'iframe[id^="genesys-messenger"]'];
const LEX_SELECTORS     = ['.chat-widget-container[data-bot="aws"]', '.chat-widget-container'];
const E8x8_SELECTORS     = ['#eightbyeight-widget', '.eightbyeight-launcher', 'iframe[src*="8x8"]'];
const OMNI2_SELECTORS    = ['[data-omnibot-widget]', '.omnibot-widget', '#omnibot-widget', 'iframe[src*="omnibots-widget"]'];
const FIVE9_SELECTORS    = ['iframe[src*="five9"]', '[id*="five9"]', '[class*="five9"]'];

/* ---------- bot UI hide/show ---------- */
const hideGenesysUI = () => {
  try {
    const w = window;
    if (typeof w.Genesys === 'function') {
      try {
        const p = w.Genesys('command', 'Messenger.close');
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch {}
    }
    hideBySelectors(GENESYS_SELECTORS);
    document.body?.classList?.remove('genesys-messenger-open', 'genesys-messenger-expanded');
    document.body?.removeAttribute('data-genesys-messenger-state');
    window.dispatchEvent(new CustomEvent('gts:botWidgetState', { detail: { isOpen: false, source: 'genesys' } }));
  } catch {}
};

const hideLexUI = () => {
  try {
    const c = document.querySelector('.chat-widget-container');
    if (c) {
      c.dataset.open = 'false';
      c.classList.remove('open', 'is-open');
      softHide(c);
      c.setAttribute('data-bot', 'aws');
    }
    window.localStorage.setItem('lexBotIsChatOpen', 'false');
    window.dispatchEvent(new CustomEvent('gts:botWidgetState', { detail: { isOpen: false, source: 'lex' } }));
  } catch {}
};

/* IMPORTANT: do not inline-hide Omni on switch-away. Just close it.
   Visibility is governed by body[data-gts-active-bot] CSS so it can reappear cleanly later. */
const hideOmniUI = () => {
  try {
    const btn = document.getElementById('mainButton');
    if (btn && btn.getAttribute('aria-expanded') === 'true' && typeof btn.click === 'function') {
      btn.click(); // close if open
    }
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent('gts:botWidgetState', { detail: { isOpen: false, source: 'omni' } }));
  } catch {}
};

const hideOmni8x8UI = () => {
  try {
    const btn = document.getElementById('mainButton');
    if (btn && btn.getAttribute('aria-expanded') === 'true' && typeof btn.click === 'function') {
      btn.click(); // close if open
    }
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent('gts:botWidgetState', { detail: { isOpen: false, source: 'omni8x8' } }));
  } catch {}
};

const hide8x8UI = () => {
  try {
    hideBySelectors(E8x8_SELECTORS);
    window.dispatchEvent(new CustomEvent('gts:botWidgetState', { detail: { isOpen: false, source: '8x8' } }));
  } catch {}
};

const showGenesysUI = () => showBySelectors(GENESYS_SELECTORS);
const showLexUI     = () => { tagLexContainer(); showBySelectors(LEX_SELECTORS); };
const showOmniUI    = () => showBySelectors(OMNI_SELECTORS);
const showOmni8x8UI = () => showBySelectors(OMNI8x8_SELECTORS);
const show8x8UI     = () => showBySelectors(E8x8_SELECTORS);

const hideFive9UI = () => {
  try {
    hideBySelectors(FIVE9_SELECTORS);
    window.dispatchEvent(new CustomEvent('gts:botWidgetState', { detail: { isOpen: false, source: 'five9' } }));
  } catch {}
};
const showFive9UI = () => showBySelectors(FIVE9_SELECTORS);

/* ---------- detect stray chat iframes and hide if not active ---------- */
const isLikelyChatIframe = (ifr) => {
  try {
    const cs = getComputedStyle(ifr);
    const w = parseInt(cs.width || '0', 10);
    const h = parseInt(cs.height || '0', 10);
    const pos = cs.position;
    const right = parseInt(cs.right || '0', 10);
    const bottom = parseInt(cs.bottom || '0', 10);
    const z = parseInt(cs.zIndex || '0', 10);
    const bubble = (w <= 120 && h <= 120) || (w >= 280 && w <= 420 && h >= 300 && h <= 700);
    const corner = (pos === 'fixed' || pos === 'absolute') && (right <= 80 || bottom <= 80);
    return bubble && corner && z >= 1000;
  } catch { return false; }
};

const belongsToActive = (ifr, active) => {
  const id = ifr.id || '';
  const src = ifr.src || '';
  if (active === 'genesys') return id.startsWith('genesys-') || src.includes('genesys');
  if (active === 'aws')     return (ifr.closest('.chat-widget-container[data-bot="aws"]') !== null);
  if (active === 'omni')    return id === 'iframe-id' || src.includes('omnibots.ai');
  if (active === 'omni8x8') return id === 'iframe-id' || src.includes('omnibots.ai');
  if (active === 'omni2')   return src.includes('omnibots-widget');
  if (active === 'omni2demo') return src.includes('omnibots-widget') || src.includes('widget.demo.dev.omnibots.ai');
  if (active === '8x8')     return src.includes('8x8') && !src.includes('omnibots.ai') && !src.includes('omnibots-widget');
  if (active === 'five9')   return src.includes('five9');
  return false;
};

const hideStrayChatIframes = (active) => {
  console.log('[Main] hideStrayChatIframes called', { active, bodyAttr: document.body?.dataset?.gtsActiveBot });
  document.querySelectorAll('iframe').forEach((ifr) => {
    if (!isLikelyChatIframe(ifr)) return;
    const belongs = belongsToActive(ifr, active);
    console.log('[Main] Checking iframe', {
      id: ifr.id,
      src: ifr.src?.substring(0, 50),
      belongs,
      active
    });
    if (belongs) {
      // CRITICAL: If iframe belongs to active bot, ensure it's visible
      // This prevents the bot from being hidden during route changes
      if (active === 'omni' || active === 'omni8x8') {
        console.log('[Main] Ensuring Omni iframe visible', { id: ifr.id });
        ifr.style.setProperty('display', 'block', 'important');
        ifr.style.setProperty('visibility', 'visible', 'important');
        ifr.style.setProperty('opacity', '1', 'important');
      }
      return;
    }
    console.log('[Main] Hiding stray iframe', { id: ifr.id, src: ifr.src?.substring(0, 50) });
    softHide(ifr);
  });
};

/* ---------- orchestrators ---------- */
const hideAllBotsExcept = (keepId) => {
  console.log('[Main] hideAllBotsExcept called', { keepId, bodyAttr: document.body?.dataset?.gtsActiveBot });
  if (keepId !== 'genesys') hideGenesysUI();
  if (keepId !== 'aws')     hideLexUI();
  if (keepId !== 'omni')    hideOmniUI();
  if (keepId !== 'omni8x8') hideOmni8x8UI();
  if (keepId !== '8x8')     hide8x8UI();
  if (keepId !== 'five9')   hideFive9UI();
  hideStrayChatIframes(keepId);
};

const showBot = (id) => {
  if (id === 'genesys') showGenesysUI();
  if (id === 'aws')     showLexUI();
  if (id === 'omni')    showOmniUI();
  if (id === 'omni8x8') showOmni8x8UI();
  if (id === '8x8')     show8x8UI();
  if (id === 'five9')   showFive9UI();
};

/* ---------- sliding tab styles ---------- */
const PANEL_WIDTH = 300;
const TAB_WIDTH = 24;
const VISIBLE_SLIVER = 25;
const PANEL_Z = 2147483000;

const basePanelStyle = {
  position: 'fixed',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  width: `${PANEL_WIDTH}px`,
  background: '#ffffff',
  color: '#003b49',
  borderRadius: '16px 0 0 16px',
  border: '1px solid rgba(0, 59, 73, 0.12)',
  boxShadow: '0 24px 48px rgba(0, 46, 57, 0.18)',
  padding: '16px 18px',
  fontFamily: '"Manrope", "Roboto", sans-serif',
  backdropFilter: 'blur(4px)',
  borderLeft: '4px solid #e75934',
  zIndex: PANEL_Z,
  transition: 'transform 180ms ease, right 180ms ease',
};

const tabHandleBase = {
  position: 'absolute',
  left: `-${TAB_WIDTH}px`,
  top: '50%',
  transform: 'translateY(-50%)',
  width: `${TAB_WIDTH}px`,
  height: '92px',
  background: '#e75934',
  color: '#ffffff',
  borderRadius: '12px 0 0 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  boxShadow: '0 10px 20px rgba(0, 0, 0, 0.18)',
};

const gripStyle = { display: 'flex', flexDirection: 'column', gap: '6px' };
const gripBarStyle = { width: '14px', height: '2px', background: '#ffffff', borderRadius: '2px' };

const labelStyle = {
  fontSize: '0.78rem',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: '#e75934',
  fontWeight: 700,
  display: 'block',
  marginBottom: '8px',
};

const selectStyle = {
  borderRadius: '12px',
  border: '1px solid rgba(231, 89, 52, 0.35)',
  backgroundColor: '#f7f9fb',
  color: '#003b49',
  padding: '10px 16px',
  fontSize: '0.95rem',
  lineHeight: 1.2,
  cursor: 'pointer',
  outline: 'none',
  appearance: 'none',
  fontWeight: 600,
  transition: 'border-color 150ms ease, box-shadow 150ms ease',
  boxShadow: '0 6px 14px rgba(0, 59, 73, 0.10)',
  width: '100%',
};

const rowStyle = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' };

const closeBtnStyle = {
  marginLeft: 'auto',
  border: '1px solid rgba(231, 89, 52, 0.35)',
  background: '#fff',
  color: '#003b49',
  borderRadius: '10px',
  padding: '6px 10px',
  fontSize: '0.85rem',
  cursor: 'pointer',
};

/* ---------- utils ---------- */
const getInitialVariant = () => {
  if (typeof window === 'undefined') return VARIANTS[0].id;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && VARIANTS.some((v) => v.id === stored)) return stored;
  } catch {}
  return VARIANTS[0].id;
};

/* ---------- sliding tab selector ---------- */
function SlidingTabSelector({ activeId, onChange }) {
  const [open, setOpen] = useState(false);

  const panelStyle = {
    ...basePanelStyle,
    transform: `translate(${open ? '0' : `${PANEL_WIDTH + TAB_WIDTH - VISIBLE_SLIVER}px`}, -50%)`,
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    setOpen(false);
  };

  return (
    <div
      id="gts-bot-selector"
      style={panelStyle}
      role="region"
      aria-label="Bot selector"
      onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
    >
      <div
        style={tabHandleBase}
        role="button"
        tabIndex={0}
        aria-label={open ? 'Close bot selector' : 'Open bot selector'}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <div style={gripStyle} aria-hidden="true">
          <span style={gripBarStyle} />
          <span style={gripBarStyle} />
          <span style={gripBarStyle} />
        </div>
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>Experience</span>
        <button type="button" style={closeBtnStyle} onClick={() => setOpen(false)} aria-label="Close selector">
          Close
        </button>
      </div>

      <select value={activeId} onChange={handleChange} style={selectStyle} aria-label="Choose header experience">
        {VARIANTS.map(({ id, label }) => <option key={id} value={id}>{label}</option>)}
      </select>
    </div>
  );
}

/* ---------- singleton guard to prevent duplicate headers ---------- */
if (typeof window !== 'undefined' && !window.__gtsHeaderSwitcherMounted) {
  window.__gtsHeaderSwitcherMounted = false;
}

/* ---------- main switcher ---------- */
function HeaderSwitcher() {
  // All hooks must be called before any conditional returns (Rules of Hooks)
  const [activeId, setActiveId] = useState(getInitialVariant);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const eventOpenRef = useRef(null);
  const mountRef = useRef(false);
  
  // Check for duplicate header after hooks are declared
  const isDuplicate = typeof window !== 'undefined' && (
    (document.getElementById('gts-main-header') && !mountRef.current) ||
    (window.__gtsHeaderSwitcherMounted && !mountRef.current)
  );
  
  useEffect(() => {
    if (isDuplicate) {
      console.warn('[HeaderSwitcher] Duplicate HeaderSwitcher detected, preventing mount');
      return;
    }
    if (mountRef.current || window.__gtsHeaderSwitcherMounted) {
      console.warn('[HeaderSwitcher] Duplicate HeaderSwitcher mount attempt detected');
      return;
    }
    mountRef.current = true;
    window.__gtsHeaderSwitcherMounted = true;
    console.log('[HeaderSwitcher] HeaderSwitcher mounted');
    
    return () => {
      mountRef.current = false;
      window.__gtsHeaderSwitcherMounted = false;
      console.log('[HeaderSwitcher] HeaderSwitcher unmounted');
    };
  }, [isDuplicate]);

  const computeWidgetOpen = useCallback(() => {
    if (typeof window === 'undefined') return false;
    const { document } = window;
    if (!document) return false;

    // Genesys
    const hasGenesysOpen =
      document.body?.classList?.contains('genesys-messenger-open') ||
      document.body?.classList?.contains('genesys-messenger-expanded') ||
      document.body?.getAttribute('data-genesys-messenger-state') === 'open';

    let genesysVisualOpen = false;
    try {
      const c = document.getElementById('genesys-messenger');
      const b = document.getElementById('genesys-messenger-button');
      const f = document.querySelector('iframe[id^="genesys-messenger"]');
      const vis = (el) => { if (!el) return false; const s = window.getComputedStyle(el); return s && s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0'; };
      genesysVisualOpen = (c && vis(c)) || (f && vis(f)) || (b && b.getAttribute('aria-expanded') === 'true');
    } catch { genesysVisualOpen = false; }

    // AWS Lex
    let hasLexOpen = false;
    try { hasLexOpen = Boolean(JSON.parse(window.localStorage.getItem('lexBotIsChatOpen') || 'false')); } catch {}
    const lexContainer = document.querySelector('.chat-widget-container[data-bot="aws"]') || document.querySelector('.chat-widget-container');
    const containerOpen =
      Boolean(lexContainer?.dataset?.open === 'true') ||
      lexContainer?.classList?.contains('open') ||
      lexContainer?.classList?.contains('is-open') ||
      Boolean(lexContainer?.querySelector('[data-chat-state="open"], .chat-window.open, .bot-widget.open'));

    // Omni Genesys (only count real open, not just the launcher)
    let hasOmniOpen = false;
    try {
      const omniBtn = document.getElementById('mainButton');
      const omniIframe = document.getElementById('iframe-id');
      const vis = (el) => { if (!el) return false; const s = window.getComputedStyle(el); return s && s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0'; };
      hasOmniOpen = (omniBtn && (omniBtn.getAttribute('aria-expanded') === 'true')) || (omniIframe && vis(omniIframe));
    } catch { hasOmniOpen = false; }

    // Omni 8x8 (only count real open, not just the launcher)
    let hasOmni8x8Open = false;
    try {
      const omni8x8Btn = document.getElementById('mainButton');
      const omni8x8Iframe = document.getElementById('iframe-id');
      const vis = (el) => { if (!el) return false; const s = window.getComputedStyle(el); return s && s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0'; };
      // Only count as open if body attribute indicates omni8x8 is active
      const isOmni8x8Active = document.body?.dataset?.gtsActiveBot === 'omni8x8';
      hasOmni8x8Open = isOmni8x8Active && ((omni8x8Btn && (omni8x8Btn.getAttribute('aria-expanded') === 'true')) || (omni8x8Iframe && vis(omni8x8Iframe)));
    } catch { hasOmni8x8Open = false; }

    // Omnibot 2.0 (check for widget elements)
    let hasOmni2Open = false;
    try {
      const isOmni2Active = document.body?.dataset?.gtsActiveBot === 'omni2';
      if (isOmni2Active) {
        const vis = (el) => { if (!el) return false; const s = window.getComputedStyle(el); return s && s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0'; };
        const omni2Widget = document.querySelector('[data-omnibot-widget], .omnibot-widget, #omnibot-widget, iframe[src*="omnibots-widget"]');
        hasOmni2Open = omni2Widget && vis(omni2Widget);
        // Also check if window.omnibot has an isOpen method
        if (!hasOmni2Open && window.omnibot && typeof window.omnibot.isOpen === 'function') {
          try {
            hasOmni2Open = window.omnibot.isOpen();
          } catch {}
        }
      }
    } catch { hasOmni2Open = false; }

    return !!(hasGenesysOpen || genesysVisualOpen || hasLexOpen || containerOpen || hasOmniOpen || hasOmni8x8Open || hasOmni2Open);
  }, []);

  useEffect(() => {
    ensureBotVisibilityStyles();
    tagLexContainer();
    if (typeof document !== 'undefined') document.body.dataset.gtsActiveBot = activeId;
  }, []);

  useEffect(() => {
    console.log('[Main] activeId effect running', { activeId, bodyAttr: document.body?.dataset?.gtsActiveBot });
    if (typeof document !== 'undefined') {
      // CRITICAL: Set body attribute IMMEDIATELY and ensure it persists
      // This is the primary mechanism for showing/hiding bots via CSS
      const prevAttr = document.body.dataset.gtsActiveBot;
      document.body.dataset.gtsActiveBot = activeId;
      if (prevAttr !== activeId) {
        console.log('[Main] Body attribute changed', { from: prevAttr, to: activeId });
      }
      
      // On route changes, immediately ensure bot visibility is maintained
      // This prevents the bot from disappearing during navigation
      if (activeId === 'omni' || activeId === 'omni8x8') {
        const ensureVisible = (attempt = 0) => {
          // CRITICAL: Re-set body attribute in case something cleared it
          const currentAttr = document.body.dataset.gtsActiveBot;
          if (currentAttr !== activeId) {
            console.warn(`[Main] Body attribute wrong (attempt ${attempt})`, {
              expected: activeId,
              actual: currentAttr
            });
            document.body.dataset.gtsActiveBot = activeId;
          }
          
          const btn = document.getElementById('mainButton');
          const iframe = document.getElementById('iframe-id');
          if (btn && iframe) {
            // BOTH bot elements exist, ensure they stay visible with !important
            [btn, iframe].forEach((el) => {
              if (el) {
                const before = {
                  display: window.getComputedStyle(el).display,
                  visibility: window.getComputedStyle(el).visibility,
                  opacity: window.getComputedStyle(el).opacity
                };
                el.style.setProperty('display', 'block', 'important');
                el.style.setProperty('visibility', 'visible', 'important');
                el.style.setProperty('opacity', '1', 'important');
                const after = {
                  display: window.getComputedStyle(el).display,
                  visibility: window.getComputedStyle(el).visibility,
                  opacity: window.getComputedStyle(el).opacity
                };
                if (attempt === 0 && (before.display === 'none' || before.visibility === 'hidden')) {
                  console.log(`[Main] Fixed hidden element for ${activeId}`, { id: el.id, before, after });
                }
              }
            });
          } else {
            // Elements don't exist - this might be why the bot disappears
            if (attempt === 0) {
              console.warn(`[Main] Bot elements missing for ${activeId}`, {
                bodyAttr: currentAttr,
                btnExists: !!btn,
                iframeExists: !!iframe
              });
            }
          }
        };
        
        // Run immediately and multiple times to catch any timing issues
        ensureVisible(0);
        setTimeout(() => ensureVisible(1), 0);
        setTimeout(() => ensureVisible(2), 50);
        setTimeout(() => ensureVisible(3), 200);
        setTimeout(() => ensureVisible(4), 500);
        setTimeout(() => ensureVisible(5), 1000);
      }
    }
  }, [activeId]);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, activeId); } catch {}
  }, [activeId]);

  const activeVariant = useMemo(() => VARIANTS.find((v) => v.id === activeId) ?? VARIANTS[0], [activeId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateState = () => {
      const heur = computeWidgetOpen();
      const next = eventOpenRef.current !== null ? eventOpenRef.current : heur;
      setIsWidgetOpen((prev) => (prev !== next ? next : prev));
    };

    updateState();

    let bodyObs, contObs, gObs, omniObs;
    const detach = (o) => { try { o?.disconnect(); } catch {} };

    const attachContObs = () => {
      detach(contObs);
      if (typeof MutationObserver === 'undefined') return;
      const c = window.document?.querySelector('.chat-widget-container');
      if (!c) return;
      contObs = new MutationObserver(updateState);
      contObs.observe(c, { attributes: true, attributeFilter: ['class', 'data-open', 'style'], childList: true, subtree: true });
    };

    const attachGObs = () => {
      detach(gObs);
      if (typeof MutationObserver === 'undefined') return;
      const g = window.document?.getElementById('genesys-messenger');
      if (!g) return;
      gObs = new MutationObserver(updateState);
      gObs.observe(g, { attributes: true, attributeFilter: ['class', 'style'], childList: true, subtree: true });
    };

    const attachOmniObs = () => {
      detach(omniObs);
      if (typeof MutationObserver === 'undefined') return;
      const o = window.document?.getElementById('iframe-id') || window.document?.getElementById('mainButton');
      if (!o) return;
      omniObs = new MutationObserver(updateState);
      omniObs.observe(o, { attributes: true, attributeFilter: ['class', 'style', 'aria-expanded'] });
    };

    if (typeof MutationObserver !== 'undefined' && window.document?.body) {
      bodyObs = new MutationObserver(() => { updateState(); attachContObs(); attachGObs(); attachOmniObs(); });
      bodyObs.observe(window.document.body, { attributes: true, attributeFilter: ['class', 'data-genesys-messenger-state'], childList: true, subtree: true });
    }

    attachContObs();
    attachGObs();
    attachOmniObs();

    const storageHandler = () => updateState();
    const customHandler = (e) => {
      if (e?.detail && typeof e.detail.isOpen === 'boolean') {
        eventOpenRef.current = e.detail.isOpen;
        setIsWidgetOpen(e.detail.isOpen);
        if (!e.detail.isOpen) updateState();
      } else {
        updateState();
      }
    };

    window.addEventListener('storage', storageHandler);
    window.addEventListener('gts:botWidgetState', customHandler);
    const id = window.setInterval(updateState, 1500);

    return () => {
      detach(bodyObs); detach(contObs); detach(gObs); detach(omniObs);
      window.removeEventListener('storage', storageHandler);
      window.removeEventListener('gts:botWidgetState', customHandler);
      window.clearInterval(id);
    };
  }, [computeWidgetOpen]);

  const handleSwitch = useCallback((nextId) => {
    if (nextId === activeId) return;

    hideAllBotsExcept(nextId);

    if (typeof document !== 'undefined') document.body.dataset.gtsActiveBot = nextId;
    if (nextId === 'aws') tagLexContainer();
    showBot(nextId);
    hideStrayChatIframes(nextId);

    eventOpenRef.current = false;
    setIsWidgetOpen(false);
    setActiveId(nextId);
  }, [activeId]);

  const BotComponent = activeVariant.botComponent;

  // Return null if duplicate detected (after ALL hooks are called)
  if (isDuplicate) {
    console.warn('[HeaderSwitcher] Duplicate HeaderSwitcher detected, returning null');
    return null;
  }

  return (
    <>
      <SharedHeader botComponent={BotComponent} activeBotId={activeId} />
      {/* Always render the selector tab. It never disappears. */}
      <SlidingTabSelector activeId={activeVariant.id} onChange={handleSwitch} />
    </>
  );
}

export default HeaderSwitcher;
